// THE MECHANISM BEHIND `Read`. One store, keyed on the resolved address.
//
// `read.ts` is the vocabulary; this is the part that fetches, shares, drops and re-reads.
// A screen never imports from here except `useRead`.
//
// # THE KEY IS THE RESOLVED ADDRESS, and that is what deletes a whole class of bug
//
// Two reads of one address are one fact, so they are one entry and one request. That is
// `useSources`' own rule — it already keys its row store on the resolved path — generalised
// from one form to the product.
//
// It also makes the 32 hand-written `let current = true` guards unnecessary rather than
// merely tidier. Those exist because one `useState` was the destination for two different
// questions, so a slow answer to an abandoned one could overwrite a fast answer to the
// current one. Keyed on the address, an answer for `/v1/people/A` cannot land in the cell
// holding `/v1/people/B`: the component is subscribed to a different entry and a late
// answer is a lookup miss rather than a race.
//
// # DEDUPLICATION AND RETENTION ARE DIFFERENT DECISIONS, and they are kept apart
//
// **Deduplication has no policy and is always on.** Two live readers of one address share
// one entry and one in-flight request. It can never show data older than the request
// currently running, so it needs no opt-out and no staleness argument.
//
// A parallel change (`perf(web): two identical reads in flight at once are one read`) puts
// the same collapse in the TRANSPORT, keyed on method and URL, and says in its own words
// that a cache "is a different decision with a different consequence" it deliberately does
// not make. The two compose rather than compete: this layer collapses two DECLARED readers
// before a request is issued at all, that one collapses whatever is left — every screen
// that has not migrated. Neither needs the other to be correct, and nothing here re-implements it.
//
// **Retention is opt-in, and there is no clock in this file.** No `staleTime`, no `gcTime`,
// no refetch on focus, no refetch on reconnect, no retry. Retention is decided by exactly
// two events, both visible in the source: the last reader leaving, and a declared write.
// A read with no `holds` is dropped when its last reader unmounts — which is exactly what
// every screen does today, and what makes migrating one behaviour-preserving.
//
// # A REFUSAL IS CACHED AS A REFUSAL, never as an empty list
//
// The entry holds the RAW outcome and `refusal` is applied at the READER. `/v1/assets` is
// legitimately read two ways — `surface` by the catalogue, `empty` by the handover picker —
// and if the empty-picker rule were applied where the answer is stored, a 403 would be held
// as `[]` for the table beside it, which reports "there is nothing here" where riksu said
// "this is not yours".
//
// # A SETTLED REFUSAL REPLACES THE VALUE. It is never kept.
//
// The tempting rule is that a re-read which fails keeps what was already on screen, and for
// a network blip that is kinder. It is wrong here, and the case is riksu's own: an
// entitlement can END. A manager holds an open step, reads what it carries, decides it —
// and the read that follows is refused because the step closed. Keeping the previous value
// would leave what they may no longer see on the screen, put there by the mechanism.
// So the previous answer survives only while a newer one is IN FLIGHT (`refreshing`), and a
// refusal that settles replaces it. Fail closed, in the file rather than per screen.

import { useCallback, useRef, useSyncExternalStore } from "react";
import { call, onWrite } from "./api";
import {
  type Address,
  type Answer,
  type AnyRead,
  type Load,
  type NeedsWhere,
  type Read,
  type Refusal,
  registeredReads,
  type Where,
} from "./read";

/** The rows a refused `empty` read offers. One frozen value, so snapshot identity holds. */
const NOTHING: readonly never[] = Object.freeze([]);

/** Nothing to ask yet. One object, for the same reason. */
const IDLE: Load<never, Refusal> = Object.freeze({ status: "idle" as const });

interface Entry {
  /**
   * The ADDRESS TEMPLATE this entry answers, and deliberately not the declaration object.
   *
   * Two declarations legitimately share one address and refuse differently —
   * `/v1/assets/holdings` is `surface` on the zimmet screen and `empty` in the record pane,
   * which is one fact asked about by two screens with two different postures. They share an
   * entry, because the key is the resolved address. Matching an invalidation on the object
   * identity of whichever declaration happened to open the entry would silently drop the
   * other one's `staleAfter` edges: the write would re-read the list for the screen that
   * mounted first and leave the pane beside it stale. Matching on the template is what makes
   * either declaration's edges reach the shared entry.
   */
  readonly at: string;
  /** Whether this entry may outlive its last reader. See `Holds`. */
  readonly holds: boolean;
  /** The holes it was resolved with, so a write can narrow to the ones it moved. */
  readonly holes: Readonly<Record<string, string>>;
  /** The RAW outcome. `refusal` is applied at the reader, never here. */
  state: "loading" | "ok" | "failed";
  value?: unknown;
  failure?: unknown;
  /** The previous answer, held only while a newer request is in flight. */
  previous?: unknown;
  /** The one request every joined reader is waiting on. */
  inflight?: Promise<void>;
  controller?: AbortController;
  /** How many mounted readers. The last one out drops the entry unless it `holds`. */
  readers: number;
  /**
   * The cached `Load` per refusal, because React requires repeated `getSnapshot` calls to
   * return the same value while the store has not changed. Two, not one, because two
   * declarations over one address may refuse differently.
   */
  snapshots: { surface?: Load<unknown, Refusal>; empty?: Load<unknown, Refusal> };
}

const entries = new Map<string, Entry>();
const listeners = new Map<string, Set<() => void>>();

/**
 * Bumped whenever ANY entry appears, answers, is re-read or is dropped.
 *
 * `useRead` does not need this — it watches one address and React re-runs `getSnapshot`
 * when that address's listener fires. `useReads` does: it composes ONE object out of
 * several entries, and React requires repeated `getSnapshot` calls to return the same
 * value while nothing has changed. A counter is what lets that composite be cached and
 * rebuilt exactly when one of its parts moved.
 */
let version = 0;

/**
 * Drops every entry and every held answer.
 *
 * Two callers, and both are obligations rather than conveniences. `src/test/setup.ts` calls
 * it in `afterEach`: a module-level store leaks between tests, and a green run served from
 * a previous test's answer is a gate that cannot fail. And it belongs on sign-out, because
 * a held catalogue is data about the tenant somebody has just left.
 */
export function forgetEverything(): void {
  for (const entry of entries.values()) {
    entry.controller?.abort();
  }
  entries.clear();
  version += 1;
}

// ------------------------------------------------------------------- addressing

/**
 * The PLAIN shape an address is resolved against: holes by name, and query parameters.
 *
 * `Where<A>` is the typed façade a call site writes — it knows which holes an address has
 * and refuses one that is missing. This is what the resolver actually operates on, and it
 * is separate because `ui/flow.tsx` resolves against a form's `values`, where the hole
 * names are a fact about the FIELDS rather than about any one address.
 */
export interface At {
  readonly path?: Readonly<Record<string, string | undefined>>;
  readonly query?: Readonly<Record<string, unknown>>;
}

/**
 * The address a read resolves to, or `null` while a hole is still open.
 *
 * An unfilled hole is NOT an address and is not read — `Source`'s rule, and the same
 * sentence covers a pane that has not been opened and a record whose id has not arrived.
 * The query is sorted, so two callers spelling the same parameters in a different order are
 * one entry rather than two.
 *
 * EXPORTED because `ui/flow.tsx` needs the identical rule for a picker's source. It used to
 * carry its own `fill` plus a brace test, which was this rule written a second time — and
 * two implementations of "is this an address yet" is exactly the drift a declaration exists
 * to prevent.
 */
export function addressOf(at: string, where: At | undefined): string | null {
  let out = "";
  let rest = at;
  const holes = where?.path ?? {};
  while (rest.length > 0) {
    const open = rest.indexOf("{");
    if (open === -1) {
      out += rest;
      break;
    }
    const close = rest.indexOf("}", open);
    if (close === -1) {
      out += rest;
      break;
    }
    const name = rest.slice(open + 1, close);
    const value = (holes[name] ?? "").trim();
    if (value === "") {
      return null;
    }
    out += rest.slice(0, open) + encodeURIComponent(value);
    rest = rest.slice(close + 1);
  }

  const query = where?.query;
  if (query === undefined) {
    return out;
  }
  const pairs = Object.entries(query)
    .filter(([, value]) => value !== undefined && value !== null && value !== "")
    .map(([name, value]) => [name, String(value)] as const)
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
  if (pairs.length === 0) {
    return out;
  }
  const search = new URLSearchParams();
  for (const [name, value] of pairs) {
    search.set(name, value);
  }
  return `${out}?${search.toString()}`;
}

/**
 * The holes a concrete path fills in a template, or `null` when it is a different route.
 *
 * EXACT, by segments, which is more than the lexical scan in `check-parity.mjs` manages:
 * that one matches `/v1/people/${` against `/v1/people/${id}/employments`, which is why its
 * `SHARED` entry over-counts a terminal-parameter route. Here a wrong segment count is a
 * different route and says so.
 */
function names(template: string, concrete: string): Record<string, string> | null {
  const wanted = template.split("/");
  const got = (concrete.split("?")[0] ?? "").split("/");
  if (wanted.length !== got.length) {
    return null;
  }
  const filled: Record<string, string> = {};
  for (let i = 0; i < wanted.length; i += 1) {
    const segment = wanted[i] ?? "";
    const against = got[i] ?? "";
    if (segment.startsWith("{") && segment.endsWith("}")) {
      filled[segment.slice(1, -1)] = decodeURIComponent(against);
    } else if (segment !== against) {
      return null;
    }
  }
  return filled;
}

// ----------------------------------------------------------------------- the store

function announce(key: string): void {
  const entry = entries.get(key);
  if (entry) {
    entry.snapshots = {};
  }
  version += 1;
  for (const listener of listeners.get(key) ?? []) {
    listener();
  }
}

function fetchInto(key: string, entry: Entry): void {
  const controller = new AbortController();
  entry.controller = controller;
  // Another write can restart an in-flight refresh. Keep its last admitted value
  // until the replacement request settles; a settled refusal still clears it below.
  entry.previous =
    entry.state === "ok" ? entry.value : entry.state === "loading" ? entry.previous : undefined;
  entry.state = "loading";

  entry.inflight = call<unknown>(key, { signal: controller.signal })
    .then((value) => {
      if (entries.get(key) !== entry || controller.signal.aborted) {
        return;
      }
      entry.state = "ok";
      entry.value = value;
      entry.failure = undefined;
      entry.previous = undefined;
      entry.inflight = undefined;
      entry.controller = undefined;
      announce(key);
    })
    .catch((failure: unknown) => {
      if (entries.get(key) !== entry || controller.signal.aborted) {
        return;
      }
      // FAIL CLOSED. The previous answer is dropped rather than kept beside the refusal —
      // see the header. `value` goes with it so no reader can reach it.
      entry.state = "failed";
      entry.failure = failure;
      entry.value = undefined;
      entry.previous = undefined;
      entry.inflight = undefined;
      entry.controller = undefined;
      announce(key);
    });
}

function open(key: string, read: AnyRead): Entry {
  const held = entries.get(key);
  if (held) {
    return held;
  }
  const entry: Entry = {
    at: read.at,
    holds: read.holds !== undefined,
    // Invalidation depends only on parameters bound by this read's address.
    // Unused form context must not suppress a successful child-resource write.
    holes: names(read.at, key) ?? {},
    state: "loading",
    readers: 0,
    snapshots: {},
  };
  entries.set(key, entry);
  version += 1;
  fetchInto(key, entry);
  return entry;
}

/** What one reader sees, given how its declaration refuses. Cached per refusal. */
function snapshotOf(entry: Entry, refusal: Refusal): Load<unknown, Refusal> {
  const cached = entry.snapshots[refusal];
  if (cached) {
    return cached;
  }
  let made: Load<unknown, Refusal>;
  if (entry.state === "loading") {
    made =
      entry.previous === undefined
        ? { status: "loading" }
        : { status: "refreshing", previous: entry.previous };
  } else if (entry.state === "ok") {
    made = { status: "loaded", value: entry.value };
  } else if (refusal === "empty") {
    // A SOURCE THAT REFUSES LEAVES AN EMPTY PICKER. Applied here, at the reader, so the
    // entry beside it that surfaces the same refusal still gets riksu's sentence.
    made = { status: "loaded", value: NOTHING };
  } else {
    made = { status: "failed", failure: entry.failure };
  }
  entry.snapshots[refusal] = made;
  return made;
}

/**
 * READ IT AGAIN, because a person asked. The retry button, and nothing else.
 *
 * This is the one re-read that is not a consequence of a write, and it is deliberately the
 * only imperative door into the store: it takes the DECLARATION and the same `Where` the
 * reader passed, so it cannot be aimed at a different address than the one that failed —
 * which is what a `reload()` closure could always do and did, four times, in this module.
 *
 * A refresh of an address nobody is reading does nothing. There is no screen to tell.
 */
export function refresh<A extends Address, F extends Refusal>(
  read: Read<A, F>,
  ...where: NeedsWhere<A> extends true ? [Where<A>] : [Where<A>?]
): void {
  refreshAsked({ read: read as AnyRead, where: where[0] as At | undefined });
}

/** Retry the same declared request held by a dynamic framework reader. */
export function refreshAsked({ read, where }: Asked): void {
  const key = addressOf(read.at, where);
  if (key === null) {
    return;
  }
  const entry = entries.get(key);
  if (entry === undefined) {
    return;
  }
  entry.controller?.abort();
  fetchInto(key, entry);
  announce(key);
}

/**
 * READ ONCE, because the answer itself must not be retained.
 *
 * A presigned document address is a credential with an expiry, not screen state. Reading
 * it through `useRead` would mint one while the pane opens and retain it in the shared
 * store; spelling its path in the click handler would abandon the declaration and the
 * contract-derived answer type. This is the narrow third shape: resolve a declared address,
 * ask only when the caller acts, return the typed answer, and keep no entry afterwards.
 */
export function readOnce<A extends Address, F extends Refusal>(
  read: Read<A, F>,
  ...where: NeedsWhere<A> extends true
    ? [Where<A>, { readonly signal?: AbortSignal }?]
    : [Where<A>?, { readonly signal?: AbortSignal }?]
): Promise<Answer<A>> {
  const key = addressOf((read as AnyRead).at, where[0] as At | undefined);
  if (key === null) {
    return Promise.reject(new Error("a declared one-time read has an unresolved address"));
  }
  return call<Answer<A>>(key, { signal: where[1]?.signal });
}

// ------------------------------------------------------------------ invalidation

/**
 * A write landed. Drop every read that named it, narrowed to the holes it moved.
 *
 * Subscribed once, at module load, to the emitter in `api.ts` — so this works for EVERY
 * write in the product on the day the store lands, including the nineteen hand-written ones
 * and the fourteen `ui/flow.tsx` drives, without one of them being edited. That is what
 * makes the migration reader-only: a list that has moved to `useRead` is correctly re-read
 * by a write in a screen nobody has touched.
 */
function movedBy(
  declaration: Pick<AnyRead, "at" | "staleAfter">,
  method: string,
  path: string,
): Record<string, string>[] {
  const moved: Record<string, string>[] = [];
  for (const edge of declaration.staleAfter ?? []) {
    const [edgeMethod, ...rest] = edge.split(" ");
    if (edgeMethod !== method) continue;
    const holes = names(rest.join(" "), path);
    if (holes !== null) moved.push(holes);
  }
  return moved;
}

/** The original declared invalidation policy, also for a signal-bound one-time consumer. */
export function readChangesOn(
  declaration: Pick<AnyRead, "at" | "staleAfter">,
  method: string,
  path: string,
): boolean {
  return movedBy(declaration, method, path).length > 0;
}

onWrite((method, path) => {
  for (const declaration of registeredReads()) {
    for (const moved of movedBy(declaration, method, path)) disturb(declaration, moved);
  }
});

/** Every live entry of one read, except those whose holes disagree with the write's. */
function disturb(read: AnyRead, moved: Record<string, string>): void {
  for (const [key, entry] of [...entries]) {
    if (entry.at !== read.at) {
      continue;
    }
    // THE NARROWING, and it is free: both sides already speak in `{hole}` templates.
    // `POST /v1/people/A/employments` drops A's employments and leaves B's alone.
    const disagrees = Object.entries(entry.holes).some(
      ([name, value]) => moved[name] !== undefined && moved[name] !== value,
    );
    if (disagrees) {
      continue;
    }
    if (entry.readers > 0) {
      // Somebody is looking at it: RE-READ, and go to `refreshing` rather than `loading`,
      // so a table does not blink back to skeletons after every create.
      entry.controller?.abort();
      fetchInto(key, entry);
      announce(key);
    } else {
      // Held past its last reader, and now stale. Drop it; the next reader re-reads.
      entry.controller?.abort();
      entries.delete(key);
      version += 1;
    }
  }
}

// ------------------------------------------------------------------------ the hooks

/**
 * One reader joins one address, and leaves when it is done.
 *
 * The whole of the refcount, in one place, because `useRead` and `useReads` must agree
 * about it exactly: two copies of "the last reader out drops the entry" is the shape where
 * a pane closing cancels the request the list behind it is waiting on.
 */
function join(key: string, declaration: AnyRead, listener: () => void): () => void {
  const entry = open(key, declaration);
  entry.readers += 1;
  const set = listeners.get(key) ?? new Set<() => void>();
  set.add(listener);
  listeners.set(key, set);

  return () => {
    set.delete(listener);
    if (set.size === 0) {
      listeners.delete(key);
    }
    const live = entries.get(key);
    if (!live) {
      return;
    }
    live.readers -= 1;
    if (live.readers > 0) {
      return;
    }
    // THE LAST READER OUT. Refcounted rather than per-component, so one pane closing
    // never cancels a request the pane behind it is waiting on.
    if (!live.holds) {
      live.controller?.abort();
      entries.delete(key);
      version += 1;
    }
  };
}

/**
 * Reads what a declaration names.
 *
 * `null` means there is nothing to ask yet — a pane that is not open, a record with no id.
 * The same convention `useQuotation(path: string | null)` already uses, and the same rule
 * `Source` states about an unfilled hole.
 */
export function useRead<A extends Address, F extends Refusal>(
  read: Read<A, F> | null,
  ...where: NeedsWhere<A> extends true ? [Where<A>] : [Where<A>?]
): Load<Answer<A>, F> {
  const declaration = read as AnyRead | null;
  // FROM THE TYPED FAÇADE TO THE PLAIN SHAPE, in one place. `Where<A>` is what makes a
  // missing hole a compile error at the call site; `At` is what the resolver reads.
  const at = where[0] as At | undefined;
  const key = declaration === null ? null : addressOf(declaration.at, at);
  // A STABLE `subscribe`, per React's own caveat: a new function identity on each render
  // makes React re-subscribe every render. Keyed on the resolved address, which is a string.
  const subscribe = useCallback(
    (listener: () => void) => {
      if (key === null || declaration === null) {
        return () => {};
      }
      return join(key, declaration, listener);
    },
    [key, declaration],
  );

  const snapshot = useCallback(() => {
    if (key === null || declaration === null) {
      return IDLE as Load<unknown, Refusal>;
    }
    const entry = entries.get(key);
    return entry === undefined
      ? (IDLE as Load<unknown, Refusal>)
      : snapshotOf(entry, declaration.refusal);
  }, [key, declaration]);

  return useSyncExternalStore(subscribe, snapshot, snapshot) as Load<Answer<A>, F>;
}

/** One declared read, and where it points. What `useReads` is handed per item. */
export interface Asked {
  readonly read: AnyRead;
  readonly where?: At;
}

/**
 * SEVERAL declared reads at once, keyed on the address each one resolved to.
 *
 * For a mechanism whose read COUNT is decided by a declaration rather than by the code:
 * `ui/flow.tsx` reads one list per `reference` field, and how many fields a flow has is
 * `Flow.fields`' business, so it cannot call `useRead` per field without breaking the rule
 * that hooks are not called in a loop. A SCREEN never needs this and should not use it — a
 * screen knows its own reads, and `useRead` says so one line at a time.
 *
 * An item whose holes are still open contributes NO entry, which is the same rule `useRead`
 * states by answering `idle`: an unfilled hole is not an address and is not read.
 *
 * The composite is cached against `version`, because React requires repeated `getSnapshot`
 * calls to return the same value while the store has not changed — a fresh object per call
 * is the documented way to make a component re-render forever.
 */
export function useReads(asked: readonly Asked[]): ReadonlyMap<string, Load<unknown, Refusal>> {
  const resolved: { key: string; read: AnyRead }[] = [];
  for (const one of asked) {
    const key = addressOf(one.read.at, one.where);
    if (key !== null && !resolved.some((held) => held.key === key)) {
      resolved.push({
        key,
        read: one.read,
      });
    }
  }
  // KEYED ON WHAT THE ADDRESSES ARE, never on the identity of the array holding them.
  // `useSources` rebuilds its list on every keystroke, so a callback keyed on the array
  // would re-subscribe on every character typed into an unrelated field. A newline cannot
  // appear in a resolved address, so joining on one is a key rather than an encoding.
  const keys = resolved.map((one) => one.key).join("\n");

  // THE LATEST DECLARATIONS, read by callbacks keyed only on the addresses. A flow's field
  // declarations are module constants and do not move while an address stays put, so the
  // address list is a complete key for them.
  const latest = useRef(resolved);
  latest.current = resolved;

  // THE RULE IS WRONG HERE AND REMOVING THE DEPENDENCY IS A BUG, not a performance note.
  // Biome sees the body read only `latest.current` — a ref, correctly never a dependency —
  // and concludes `keys` is surplus. But `keys` is the ONLY thing that may change this
  // callback's identity, and React re-subscribes exactly when it does. Drop it and
  // `subscribe` is stable forever, so a form whose dependent picker moves to a new address
  // — the employments of the person just chosen — stays subscribed to the previous one and
  // renders the previous person's employments. That is the defect `flow.tsx`'s own clearing
  // test was written for, one layer down.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `keys` is the re-subscribe key; see above.
  const subscribe = useCallback(
    (listener: () => void) => {
      const leaving = latest.current.map((one) => join(one.key, one.read, listener));
      return () => {
        for (const leave of leaving) {
          leave();
        }
      };
    },
    [keys],
  );

  const cached = useRef<{
    at: number;
    keys: string;
    made: ReadonlyMap<string, Load<unknown, Refusal>>;
  }>({ at: -1, keys: "", made: new Map() });

  const snapshot = useCallback(() => {
    if (cached.current.at === version && cached.current.keys === keys) {
      return cached.current.made;
    }
    const made = new Map<string, Load<unknown, Refusal>>();
    for (const one of latest.current) {
      const entry = entries.get(one.key);
      made.set(one.key, entry === undefined ? IDLE : snapshotOf(entry, one.read.refusal));
    }
    cached.current = { at: version, keys, made };
    return made;
  }, [keys]);

  return useSyncExternalStore(subscribe, snapshot, snapshot);
}
